import { SwitchLocale } from '@/client/components/switch-locale';
import { NavNotification } from '../components/nav-notification';
import { UserInfo } from '../components/use-info';
import { InteractLeaderboardIcon, ViewRewardsIcon } from '@/client/assets';
import { AuthedButton } from '@/client/components/authed-button';
import { REFERRAL_ENABLED } from '@/client/confs/app';
import { SearchButton } from '@/client/components/search/search-button';
import { SwitchBaseToken } from '@/client/components/switch-base-token';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';
import { useSignedUserInfo } from '@/client/hooks/use-signed-user-info';
import { AppTitle } from '@/client/layouts/components/app-title';
import { useModelStore } from '@/client/store/model-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useRoute } from 'wouter';

export const TheMarketHeader = () => {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const { data: signer } = useSignedUserInfo(api);
  const setHowItWorksOpen = useModelStore(state => state.setHowItWorksOpen);
  const rewardsLink = useMemo(() => `/user/${signer?.uid}/invite-earn`, [signer?.uid]);
  const [isLeaderboardActive] = useRoute('/leaderboard');
  const [isRewardsActive] = useRoute('/user/:uid/invite-earn');
  const [isMarketsActive] = useRoute('/markets');
  const [isPortfolioActive] = useRoute('/portfolio');
  const [isHomeActive] = useRoute('/');

  return (
    <header
      className={cn(
        'w-full border-b border-foreground/4 bg-background/95 backdrop-blur-md h-13',
        'md:(h-15 pos-sticky top-0 z-50)'
      )}
    >
      <div className="w-full flex items-center justify-between size-full mx-auto max-w-[1400px] px-4 md:px-6 gap-4">
        {/* Left side: Logo. min-w-0 lets this flex track shrink below its content
            so the fixed right cluster doesn't push the row past the viewport at the
            md→lg band (was causing 143px horizontal scroll on iPad-portrait widths). */}
        <div className="flex items-center justify-start gap-6 flex-1 min-w-0">
          <AppTitle />
          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={cn(
                'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                isHomeActive ? 'text-foreground bg-foreground/5' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {t('home', 'Home')}
            </Link>
            <Link
              to="/markets"
              className={cn(
                'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                isMarketsActive ? 'text-foreground bg-foreground/5' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {t('markets', 'Markets')}
            </Link>
            <Link
              to="/portfolio"
              className={cn(
                'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                isPortfolioActive ? 'text-foreground bg-foreground/5' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {t('portfolio', 'Portfolio')}
            </Link>
            <button
              type="button"
              onClick={() => setHowItWorksOpen(true)}
              className="px-3 py-1.5 rounded-md text-sm font-medium transition-colors text-muted-foreground hover:text-foreground cursor-pointer"
            >
              {t('how-it-works', 'How it works')}
            </button>
          </nav>
        </div>

        {/* Center: Search. Shrinkable (flex-1 + min-w-0, capped at 320) instead of a
            fixed 320px slot so it yields space in the md→lg band rather than forcing
            overflow. */}
        <div className="hidden md:flex md:flex-1 md:max-w-[320px] md:min-w-0">
          <SearchButton />
        </div>

        {/* Right side: Nav + Actions. shrink-0 = natural width that never shrinks, so
            the left/center tracks absorb the squeeze. */}
        <div className="flex items-center justify-end gap-3 shrink-0">
          {/* Currency lens sits with the account cluster: it denominates the
              balance shown here, and top-right is where users expect a
              currency/mode picker — left of nav it reads as a feed filter. */}
          <SwitchBaseToken />

          {/* Leaderboard */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/leaderboard"
                aria-label={t('leaderboard')}
                className={cn(
                  // lg: (not md:) — deferring these two supplementary icons past the
                  // 768–1024 band keeps the header within iPad-portrait widths.
                  'hidden lg:flex items-center justify-center size-9 rounded-md cursor-pointer transition-colors border border-foreground/4',
                  isLeaderboardActive ? 'bg-foreground/5' : 'bg-transparent hover:bg-foreground/5'
                )}
              >
                <InteractLeaderboardIcon className="size-5 fill-foreground" />
              </Link>
            </TooltipTrigger>
            <TooltipContent>{t('leaderboard')}</TooltipContent>
          </Tooltip>

          {/* Referral earnings */}
          {REFERRAL_ENABLED && (
            <Tooltip>
              <TooltipTrigger asChild>
                <AuthedButton
                  showAuth={false}
                  showLoading={false}
                  onClick={() => navigate(rewardsLink)}
                  variant="outline"
                  size="icon"
                  aria-label={t('rewards')}
                  className={cn(
                    'hidden lg:flex p-2 border border-foreground/4 shadow-none',
                    isRewardsActive ? 'bg-foreground/5' : 'bg-transparent hover:bg-foreground/5'
                  )}
                >
                  <ViewRewardsIcon className="size-5 fill-primary" />
                </AuthedButton>
              </TooltipTrigger>
              <TooltipContent>{t('rewards')}</TooltipContent>
            </Tooltip>
          )}

          {/* Language: globe in the top-right so mobile users on the wrong
              default locale can find it (previously desktop-footer only). */}
          <SwitchLocale />

          <NavNotification />
          <UserInfo />
        </div>
      </div>
    </header>
  );
};
